import type { PortableTextBlock } from '@portabletext/types';
import type {
  InternalLink,
  Locale,
  LocalePortableText,
  LocaleSlug,
  LocaleString,
  LocaleText,
} from '~/lib/types/blocks';
import { DEFAULT_LOCALE } from '~/lib/i18n';

const OTHER_LOCALE: Record<Locale, Locale> = { nl: 'en', en: 'nl' };

/**
 * @param field - A locale-aware string or text field
 * @param locale - The active locale to resolve
 * @returns The resolved string value, falling back to the other locale, or undefined
 */
export function resolveLocale(
  field: LocaleString | LocaleText | undefined,
  locale: Locale,
): string | undefined;
/**
 * @param field - A locale-aware portable text field
 * @param locale - The active locale to resolve
 * @returns The resolved portable text blocks, falling back to the other locale, or undefined
 */
export function resolveLocale(
  field: LocalePortableText | undefined,
  locale: Locale,
): PortableTextBlock[] | undefined;
export function resolveLocale(
  field: LocaleString | LocaleText | LocalePortableText | undefined,
  locale: Locale,
): string | PortableTextBlock[] | undefined {
  if (!field) {
    return undefined;
  }

  const value = field[locale];
  if (isTruthy(value)) {
    return value;
  }

  const fallback = field[OTHER_LOCALE[locale]];
  if (isTruthy(fallback)) {
    return fallback;
  }

  return undefined;
}

function isTruthy(value: string | PortableTextBlock[] | undefined | null): boolean {
  if (null == value) {
    return false;
  }
  if ('string' === typeof value) {
    return 0 < value.length;
  }
  if (Array.isArray(value)) {
    return 0 < value.length;
  }
  return false;
}

/**
 * @param slug - A localeSlug field (Dutch is required; English falls back to it when empty)
 * @param locale - The active locale to resolve
 * @returns The slug for that locale, or undefined when neither is set
 */
export function resolveSlug(slug: LocaleSlug | undefined, locale: Locale): string | undefined {
  const value = slug?.[locale]?.current ?? slug?.[OTHER_LOCALE[locale]]?.current;
  if (null == value || 0 === value.length) {
    return undefined;
  }

  return value;
}

/**
 * The default locale lives at the root and every other locale under its own segment, so a
 * page's path is `/<slug>` in English and `/nl/<slug>` in Dutch.
 *
 * @param link - A dereferenced internal link (`internalLink->{ _type, slug }`)
 * @param locale - The active locale
 * @returns The site-relative path to the linked page, or undefined when it has no slug
 */
export function internalLinkPath(link: InternalLink | null | undefined, locale: Locale): string | undefined {
  const slug = resolveSlug(link?.slug, locale);
  if (undefined === slug) {
    return undefined;
  }

  return DEFAULT_LOCALE === locale ? `/${slug}` : `/${locale}/${slug}`;
}
